/**
 * Knowledge Sources panel for SettingsPage. Aligns PP main #131 era.
 *
 * Renders a row per registered connector (Confluence, Droploft) with
 * its current connection status + a connect/disconnect button. PP
 * main's version hits a /api/connectors registry endpoint; PP-CF has
 * only two connectors today so we hardcode the metadata to avoid the
 * extra route + worker code path.
 *
 * Connection flow per connector:
 *   - Confluence: OAuth — opens the existing app-level Confluence
 *     connect popup. Uses a window.dispatchEvent shim so the user
 *     can drive the OAuth from Settings the same as from the deck
 *     toolbar.
 *   - Droploft: API key paste — opens an inline dialog that pops
 *     the connector's setupUrl and accepts a pasted key.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  SpinnerGap, Check, Link as LinkIcon, LinkBreak, BookOpen, X, ArrowSquareOut,
} from '@phosphor-icons/react';
import * as api from '../api/client';

interface ConnectorRow {
  id: 'confluence' | 'droploft';
  label: string;
  description: string;
  auth: 'oauth' | 'apikey';
  setupUrl?: string;
  setupHint?: string;
}

const CONNECTORS: ConnectorRow[] = [
  {
    id: 'confluence',
    label: 'Confluence',
    description: 'Reference Atlassian Confluence pages from any deck.',
    auth: 'oauth',
  },
  {
    id: 'droploft',
    label: 'Droploft',
    description: 'Reference and publish Droploft documents from any deck.',
    auth: 'apikey',
    setupUrl: 'https://stage-a.droploft.ai/settings',
    setupHint: 'Generate an API key in your Droploft account settings.',
  },
];

interface State {
  loading: boolean;
  connected: boolean;
  err?: string | null;
}

export default function KnowledgePanel() {
  const [states, setStates] = useState<Record<string, State>>({});
  const [paste, setPaste] = useState<ConnectorRow | null>(null);
  const [pasteKey, setPasteKey] = useState('');
  const [paseError, setPasteError] = useState<string | null>(null);
  const [pasting, setPasting] = useState(false);

  const refresh = useCallback(async (id: 'confluence' | 'droploft') => {
    setStates((s) => ({ ...s, [id]: { ...(s[id] || {}), loading: true } }));
    try {
      const connections = id === 'confluence'
        ? await api.listConfluenceConnections()
        : await api.listDroploftConnections();
      setStates((s) => ({
        ...s,
        [id]: { loading: false, connected: Array.isArray(connections) && connections.length > 0 },
      }));
    } catch {
      setStates((s) => ({ ...s, [id]: { loading: false, connected: false, err: 'load failed' } }));
    }
  }, []);

  useEffect(() => {
    void refresh('confluence');
    void refresh('droploft');
  }, [refresh]);

  const handleConnect = async (c: ConnectorRow) => {
    if (c.auth === 'oauth') {
      // Confluence OAuth — the existing app-level handler owns the
      // popup + postMessage exchange. Dispatch a custom event the
      // App.tsx listens to. (Falls back to opening the route directly
      // when listener isn't wired yet — fine for first-load.)
      const opened = new Event('pp:open-confluence-connect');
      window.dispatchEvent(opened);
      // After dispatch, wait briefly + refresh to pick up the new
      // connection if the popup completed.
      setTimeout(() => void refresh('confluence'), 8000);
      return;
    }
    // apikey flow → inline paste dialog
    setPaste(c);
    setPasteKey('');
    setPasteError(null);
  };

  const handleDisconnect = async (id: 'confluence' | 'droploft') => {
    if (!window.confirm(`Disconnect ${id}?`)) return;
    setStates((s) => ({ ...s, [id]: { ...(s[id] || {}), loading: true } }));
    try {
      if (id === 'confluence') {
        const conns = await api.listConfluenceConnections();
        for (const c of conns) {
          await api.deleteConfluenceConnection((c as { id: string }).id);
        }
      } else {
        const conns = await api.listDroploftConnections();
        for (const c of conns) {
          await api.deleteDroploftConnection(c.id);
        }
      }
    } catch {}
    await refresh(id);
  };

  const submitPaste = async () => {
    if (!paste) return;
    setPasting(true);
    setPasteError(null);
    try {
      if (paste.id === 'droploft') {
        await api.addDroploftConnection({ apiKey: pasteKey.trim() });
      }
      setPaste(null);
      setPasteKey('');
      await refresh(paste.id);
    } catch (e: any) {
      setPasteError(e?.message || 'Failed to save key');
    } finally {
      setPasting(false);
    }
  };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 12,
      background: 'var(--bg-surface)', padding: '20px 24px',
      marginTop: 24,
    }}>
      <h3 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <BookOpen size={16} weight="light" /> Knowledge sources
      </h3>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginTop: 4, marginBottom: 16 }}>
        External knowledge bases the agent can reference. Connecting here makes the
        connector available from every deck's toolbar.
      </p>

      {CONNECTORS.map((c) => {
        const s = states[c.id] ?? { loading: true, connected: false };
        return (
          <div key={c.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, padding: '12px 0', borderBottom: '1px solid var(--border)',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {c.label}
                </span>
                {s.loading ? (
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>checking…</span>
                ) : s.connected ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 10, fontSize: 'var(--fs-2xs)', fontWeight: 600, background: 'rgba(34,197,94,0.12)', color: '#16a34a' }}>
                    <Check size={10} weight="bold" /> Connected
                  </span>
                ) : (
                  <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>not connected</span>
                )}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                {c.description}
              </div>
            </div>
            {s.loading ? (
              <SpinnerGap size={14} weight="light" className="animate-spin" />
            ) : s.connected ? (
              <button
                onClick={() => handleDisconnect(c.id)}
                title={`Disconnect ${c.label}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 12px', fontSize: 'var(--fs-sm)',
                  background: 'transparent', color: 'var(--text-secondary)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <LinkBreak size={12} weight="light" /> Disconnect
              </button>
            ) : (
              <button
                onClick={() => void handleConnect(c)}
                title={`Connect ${c.label}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '6px 12px', fontSize: 'var(--fs-sm)', fontWeight: 600,
                  background: '#0033FF', color: 'white',
                  border: 'none', borderRadius: 6,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <LinkIcon size={12} weight="bold" /> Connect
              </button>
            )}
          </div>
        );
      })}

      {/* Inline API-key paste dialog */}
      {paste && (
        <div
          onClick={() => setPaste(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 600,
            background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 420, maxWidth: '90vw',
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 12, padding: 24,
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h4 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 600 }}>Connect {paste.label}</h4>
              <button onClick={() => setPaste(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                <X size={14} weight="light" />
              </button>
            </div>
            {paste.setupHint && (
              <p style={{ marginTop: 0, marginBottom: 12, fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                {paste.setupHint}
              </p>
            )}
            {paste.setupUrl && (
              <a
                href={paste.setupUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '6px 12px', fontSize: 'var(--fs-sm)',
                  background: 'transparent', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  textDecoration: 'none', marginBottom: 12,
                }}
              >
                <ArrowSquareOut size={12} weight="light" /> Open {paste.label} settings
              </a>
            )}
            <input
              type="password"
              value={pasteKey}
              onChange={(e) => setPasteKey(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void submitPaste(); }}
              placeholder="Paste API key"
              autoFocus
              style={{
                width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)',
                border: '1px solid var(--border)', borderRadius: 8,
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box', marginBottom: 8,
              }}
            />
            {paseError && (
              <div style={{ fontSize: 'var(--fs-xs)', color: '#ef4444', marginBottom: 8 }}>
                {paseError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setPaste(null)} style={{ padding: '6px 12px', fontSize: 'var(--fs-sm)', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                onClick={() => void submitPaste()}
                disabled={pasting || pasteKey.trim().length < 8}
                style={{ padding: '6px 14px', fontSize: 'var(--fs-sm)', fontWeight: 600, background: pasting || pasteKey.trim().length < 8 ? 'var(--border)' : '#0033FF', color: 'white', border: 'none', borderRadius: 8, cursor: pasting || pasteKey.trim().length < 8 ? 'default' : 'pointer', fontFamily: 'inherit' }}
              >
                {pasting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
